"use client";

/**
 * Bulk Assign Instructors from แผนเปิดรายวิชา 2568
 *
 * Source: User-provided schedule for 1/68 and 2/68 (กศ.บ. เทคโนโลยีการศึกษา+คอมพิวเตอร์ศึกษา)
 *
 * Flow:
 *   1. Ensure all instructors have user docs (create placeholders if not)
 *   2. Assign instructorUid + instructorName to each course
 *   3. When real instructor logs in, auto-link placeholder → real user via displayName match
 */
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, writeBatch, serverTimestamp,
  Timestamp, query, where,
  type QueryDocumentSnapshot, type DocumentData,
} from "firebase/firestore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getFirebase } from "./client";

// ============ Instructors in 2568 ============

export interface InstructorRecord {
  slug: string;          // for placeholder uid
  name: string;          // full displayName
  email?: string;        // MSU email (for auto-link)
  dept: "ETC" | "IT" | "Other";
}

export const INSTRUCTORS_2568: InstructorRecord[] = [
  { slug: "thapani-seechaliao",        name: "รศ.ดร.ฐาปนี สีเฉลียว",        email: "thapanee.see@msu.ac.th",    dept: "ETC" },
  { slug: "ratchaneewan-tangpakdee",   name: "รศ.ดร.รัชนีวรรณ ตั้งภักดี",   email: "ratchaneewan.t@msu.ac.th",  dept: "ETC" },
  { slug: "manit-arsanok",             name: "ผศ.ดร.มานิตย์ อาษานอก",       email: "manit.a@msu.ac.th",         dept: "ETC" },
  { slug: "thanadol-phusrithit",       name: "ผศ.ดร.ธนดล ภูสีฤทธิ์",                                             dept: "ETC" },
  { slug: "rathasan-laohasurayotin",   name: "ผศ.ดร.รัฐสาน์ เลาหสุรโยธิน",  email: "ratasa.l@msu.ac.th",        dept: "ETC" },
  { slug: "korkiat-kwansakul",         name: "ผศ.ก่อเกียรติ ขวัญสกุล",      email: "kokeit.k@msu.ac.th",        dept: "ETC" },
  { slug: "manawika-kittiporn",        name: "อ.ดร.มาณวิกา กิตติพร",        email: "manwika.k@msu.ac.th",       dept: "ETC" },
  { slug: "hemminin-thanapatmeemanee", name: "อ.ดร.เหมมิญช์ ธนปัทม์มีมณี",  email: "hemmin.t@msu.ac.th",        dept: "ETC" },
  { slug: "borworn-srimaan",           name: "อ.ดร.บวรรัตน์ ศรีมาน",         email: "bowornrat.s@msu.ac.th",     dept: "ETC" },
  { slug: "panida-songrum",            name: "รศ.ดร.พนิดา ทรงรัมย์",         email: "panida.s@msu.ac.th",        dept: "IT" },
  { slug: "pimonrat-uansrimuang",      name: "ผศ.พิมลรัตน์ อ้วนศรีเมือง",                                       dept: "IT" },
  { slug: "napat-saktong",             name: "อ.ณภัทร สักทอง",                                                   dept: "IT" },
  { slug: "tawatchawong-lawan",        name: "อ.ธวัชวงศ์ ลาวัลย์",           email: "thawatwong@msu.ac.th",      dept: "IT" },
];

// ============ Course → Sections × Instructors mapping ============
// Each course can have multiple sections, each with own instructor(s)
// "primary" = the coordinator/first listed (used as instructorUid for backward compat)

export interface SectionAssignment {
  section: number;
  slug: string;
  semester: string;        // "1/68" or "2/68"
}

export const COURSE_ASSIGNMENTS_2568: Array<{
  code: string;
  sections: SectionAssignment[];
  note?: string;
}> = [
  // ═══════ 1/68 ═══════
  // Year 1 — General/foundational
  { code: "0530 101", note: "Coordinator only — actually 12 sections × 9 instructors", sections: [
    { section: 1, slug: "ratchaneewan-tangpakdee", semester: "1/68" },
    { section: 2, slug: "ratchaneewan-tangpakdee", semester: "1/68" },
    { section: 3, slug: "rathasan-laohasurayotin", semester: "1/68" },
    { section: 4, slug: "rathasan-laohasurayotin", semester: "1/68" },
    { section: 5, slug: "manit-arsanok",          semester: "1/68" },
    { section: 6, slug: "borworn-srimaan",        semester: "1/68" },
    { section: 7, slug: "manawika-kittiporn",     semester: "1/68" },
    { section: 8, slug: "thapani-seechaliao",     semester: "1/68" },
    { section: 9, slug: "thapani-seechaliao",     semester: "1/68" },
    { section: 10, slug: "thapani-seechaliao",    semester: "1/68" },
    { section: 11, slug: "thanadol-phusrithit",   semester: "1/68" },
    { section: 12, slug: "thanadol-phusrithit",   semester: "1/68" },
    { section: 13, slug: "manawika-kittiporn",    semester: "1/68" },
    { section: 14, slug: "manawika-kittiporn",    semester: "1/68" },
    { section: 15, slug: "borworn-srimaan",       semester: "1/68" },
    { section: 16, slug: "borworn-srimaan",       semester: "1/68" },
    { section: 17, slug: "korkiat-kwansakul",     semester: "1/68" },
    { section: 18, slug: "korkiat-kwansakul",     semester: "1/68" },
    { section: 19, slug: "manit-arsanok",         semester: "1/68" },
    { section: 20, slug: "manit-arsanok",         semester: "1/68" },
    { section: 21, slug: "thanadol-phusrithit",   semester: "1/68" },
    { section: 22, slug: "thanadol-phusrithit",   semester: "1/68" },
    { section: 23, slug: "hemminin-thanapatmeemanee", semester: "1/68" },
    { section: 24, slug: "hemminin-thanapatmeemanee", semester: "1/68" },
    { section: 25, slug: "hemminin-thanapatmeemanee", semester: "1/68" },
  ]},
  { code: "0537 112", sections: [
    { section: 1, slug: "manawika-kittiporn", semester: "1/68" },
    { section: 2, slug: "manawika-kittiporn", semester: "1/68" },
  ]},
  { code: "0537 113", sections: [
    { section: 1, slug: "thapani-seechaliao", semester: "1/68" },
    { section: 2, slug: "thapani-seechaliao", semester: "1/68" },
  ]},
  { code: "0537 114", sections: [
    { section: 1, slug: "korkiat-kwansakul", semester: "1/68" },
    { section: 2, slug: "korkiat-kwansakul", semester: "1/68" },
  ]},
  { code: "0537 115", sections: [
    { section: 1, slug: "thanadol-phusrithit", semester: "1/68" },
    { section: 2, slug: "thanadol-phusrithit", semester: "1/68" },
  ]},
  { code: "1204 437", sections: [
    { section: 1, slug: "napat-saktong", semester: "1/68" },
    { section: 2, slug: "napat-saktong", semester: "1/68" },
  ]},
  // Year 2 (1/68)
  { code: "0537 211", note: "3 sections × 3 instructors", sections: [
    { section: 1, slug: "ratchaneewan-tangpakdee", semester: "1/68" },
    { section: 2, slug: "thapani-seechaliao",      semester: "1/68" },
    { section: 3, slug: "manit-arsanok",            semester: "1/68" },
  ]},
  { code: "0537 212", sections: [
    { section: 1, slug: "rathasan-laohasurayotin", semester: "1/68" },
    { section: 2, slug: "rathasan-laohasurayotin", semester: "1/68" },
  ]},
  { code: "0537 338", sections: [
    { section: 1, slug: "borworn-srimaan", semester: "1/68" },
    { section: 2, slug: "borworn-srimaan", semester: "1/68" },
  ]},
  { code: "1204 442", sections: [
    { section: 1, slug: "panida-songrum", semester: "1/68" },
    { section: 2, slug: "panida-songrum", semester: "1/68" },
  ]},
  // Year 3 (1/68)
  { code: "0560 301", sections: [
    { section: 1, slug: "manit-arsanok", semester: "1/68" },
    { section: 2, slug: "manit-arsanok", semester: "1/68" },
  ]},
  { code: "0537 319", sections: [
    { section: 1, slug: "rathasan-laohasurayotin", semester: "1/68" },
    { section: 2, slug: "rathasan-laohasurayotin", semester: "1/68" },
  ]},
  { code: "0537 320", note: "legacy course (หลักสูตร 2562)", sections: [
    { section: 1, slug: "hemminin-thanapatmeemanee", semester: "1/68" },
    { section: 2, slug: "hemminin-thanapatmeemanee", semester: "1/68" },
  ]},
  { code: "0537 335", note: "co-taught both sections", sections: [
    { section: 1, slug: "manawika-kittiporn", semester: "1/68" },
    { section: 2, slug: "borworn-srimaan",    semester: "1/68" },
  ]},
  { code: "0537 343", sections: [
    { section: 1, slug: "ratchaneewan-tangpakdee", semester: "1/68" },
    { section: 2, slug: "ratchaneewan-tangpakdee", semester: "1/68" },
  ]},
  // Year 4 (1/68) — Internship 1
  { code: "0560 401", note: "9 sections × 9 instructors", sections: [
    { section: 1, slug: "thanadol-phusrithit",        semester: "1/68" },
    { section: 2, slug: "thapani-seechaliao",         semester: "1/68" },
    { section: 3, slug: "manit-arsanok",              semester: "1/68" },
    { section: 4, slug: "hemminin-thanapatmeemanee",  semester: "1/68" },
    { section: 5, slug: "korkiat-kwansakul",          semester: "1/68" },
    { section: 6, slug: "rathasan-laohasurayotin",    semester: "1/68" },
    { section: 7, slug: "ratchaneewan-tangpakdee",    semester: "1/68" },
    { section: 8, slug: "manawika-kittiporn",         semester: "1/68" },
    { section: 9, slug: "borworn-srimaan",            semester: "1/68" },
  ]},

  // ═══════ 2/68 ═══════
  // Year 1
  { code: "0537 116", sections: [
    { section: 1, slug: "thanadol-phusrithit",        semester: "2/68" },
    { section: 2, slug: "hemminin-thanapatmeemanee",  semester: "2/68" },
  ]},
  { code: "0537 117", sections: [
    { section: 1, slug: "korkiat-kwansakul", semester: "2/68" },
    { section: 2, slug: "korkiat-kwansakul", semester: "2/68" },
  ]},
  { code: "0537 334", sections: [
    { section: 1, slug: "manawika-kittiporn", semester: "2/68" },
    { section: 2, slug: "manit-arsanok",       semester: "2/68" },
  ]},
  { code: "1204 440", note: "co-taught", sections: [
    { section: 1, slug: "borworn-srimaan",     semester: "2/68" },
    { section: 2, slug: "tawatchawong-lawan",  semester: "2/68" },
  ]},
  // Year 2 (2/68)
  { code: "0537 213", note: "4 sections × 4 instructors", sections: [
    { section: 1, slug: "thapani-seechaliao",       semester: "2/68" },
    { section: 2, slug: "ratchaneewan-tangpakdee",  semester: "2/68" },
    { section: 3, slug: "manit-arsanok",            semester: "2/68" },
    { section: 4, slug: "thanadol-phusrithit",      semester: "2/68" },
  ]},
  { code: "0537 214", sections: [
    { section: 1, slug: "korkiat-kwansakul",    semester: "2/68" },
    { section: 2, slug: "thanadol-phusrithit",  semester: "2/68" },
  ]},
  { code: "0537 336", note: "Robotics — 2 sections", sections: [
    { section: 1, slug: "manawika-kittiporn",   semester: "2/68" },
    { section: 2, slug: "manit-arsanok",        semester: "2/68" },
  ]},
  { code: "1204 443", sections: [
    { section: 1, slug: "pimonrat-uansrimuang", semester: "2/68" },
    { section: 2, slug: "pimonrat-uansrimuang", semester: "2/68" },
  ]},
  // Year 3 (2/68)
  { code: "0537 332", sections: [
    { section: 1, slug: "ratchaneewan-tangpakdee", semester: "2/68" },
    { section: 2, slug: "ratchaneewan-tangpakdee", semester: "2/68" },
  ]},
  { code: "0537 333", note: "9 sections × 9 instructors", sections: [
    { section: 1, slug: "manawika-kittiporn",        semester: "2/68" },
    { section: 2, slug: "thapani-seechaliao",        semester: "2/68" },
    { section: 3, slug: "hemminin-thanapatmeemanee", semester: "2/68" },
    { section: 4, slug: "korkiat-kwansakul",         semester: "2/68" },
    { section: 5, slug: "rathasan-laohasurayotin",   semester: "2/68" },
    { section: 6, slug: "ratchaneewan-tangpakdee",   semester: "2/68" },
    { section: 7, slug: "thanadol-phusrithit",       semester: "2/68" },
    { section: 8, slug: "manit-arsanok",             semester: "2/68" },
    { section: 9, slug: "borworn-srimaan",           semester: "2/68" },
  ]},
  { code: "0537 337", note: "9 sections × 9 instructors", sections: [
    { section: 1, slug: "manawika-kittiporn",        semester: "2/68" },
    { section: 2, slug: "thapani-seechaliao",        semester: "2/68" },
    { section: 3, slug: "hemminin-thanapatmeemanee", semester: "2/68" },
    { section: 4, slug: "korkiat-kwansakul",         semester: "2/68" },
    { section: 5, slug: "rathasan-laohasurayotin",   semester: "2/68" },
    { section: 6, slug: "ratchaneewan-tangpakdee",   semester: "2/68" },
    { section: 7, slug: "thanadol-phusrithit",       semester: "2/68" },
    { section: 8, slug: "manit-arsanok",             semester: "2/68" },
    { section: 9, slug: "borworn-srimaan",           semester: "2/68" },
  ]},
  { code: "0537 342", note: "Practicum — 9 sections", sections: [
    { section: 1, slug: "manawika-kittiporn",        semester: "2/68" },
    { section: 2, slug: "thapani-seechaliao",        semester: "2/68" },
    { section: 3, slug: "hemminin-thanapatmeemanee", semester: "2/68" },
    { section: 4, slug: "korkiat-kwansakul",         semester: "2/68" },
    { section: 5, slug: "rathasan-laohasurayotin",   semester: "2/68" },
    { section: 6, slug: "ratchaneewan-tangpakdee",   semester: "2/68" },
    { section: 7, slug: "thanadol-phusrithit",       semester: "2/68" },
    { section: 8, slug: "manit-arsanok",             semester: "2/68" },
    { section: 9, slug: "borworn-srimaan",           semester: "2/68" },
  ]},
  // Year 4 (2/68) — Internship 2
  { code: "0560 402", note: "Internship 2 — 11 sections", sections: [
    { section: 1,  slug: "manawika-kittiporn",       semester: "2/68" },
    { section: 2,  slug: "thapani-seechaliao",       semester: "2/68" },
    { section: 3,  slug: "manit-arsanok",            semester: "2/68" },
    { section: 4,  slug: "hemminin-thanapatmeemanee", semester: "2/68" },
    { section: 5,  slug: "korkiat-kwansakul",        semester: "2/68" },
    { section: 6,  slug: "rathasan-laohasurayotin",  semester: "2/68" },
    { section: 7,  slug: "ratchaneewan-tangpakdee",  semester: "2/68" },
    { section: 8,  slug: "thanadol-phusrithit",      semester: "2/68" },
    { section: 9,  slug: "borworn-srimaan",          semester: "2/68" },
    { section: 10, slug: "borworn-srimaan",          semester: "2/68" },
    { section: 11, slug: "borworn-srimaan",          semester: "2/68" },
  ]},
];

// ============ Hook ============

/** Re-sync real users' displayNames from INSTRUCTORS_2568 catalog by email match. */
export function useSyncInstructorNames() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await syncDisplayNamesFromCatalog();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users-list"] });
    },
  });
}

export function useBulkAssignFromSchedule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      programId: string;
      currentUserUid: string;
      currentUserName: string;
    }) => {
      const { db } = getFirebase();

      // 1. Read existing users to find matches (by email > placeholderSlug > displayName)
      const usersSnap = await getDocs(collection(db, "users"));
      const slugToUid = new Map<string, string>();
      for (const u of usersSnap.docs) {
        const data = u.data();
        const displayName = (data.displayName ?? "") as string;
        const email = ((data.email ?? "") as string).toLowerCase();
        const slug = (data.placeholderSlug ?? "") as string;

        if (slug) slugToUid.set(slug, u.id);

        for (const instr of INSTRUCTORS_2568) {
          // Match by email first (most reliable)
          if (instr.email && email === instr.email.toLowerCase()) {
            slugToUid.set(instr.slug, u.id);
          }
          // Fallback: match by displayName
          else if (displayName === instr.name) {
            slugToUid.set(instr.slug, u.id);
          }
        }
      }

      // Special case: current logged-in user (Manit) — use their actual UID
      // Use email match if possible
      const currentUserEmail = ""; // We don't pass email here; rely on the users-collection scan above
      void currentUserEmail;
      if (args.currentUserName.includes("มานิตย์") || args.currentUserName.includes("Manit")) {
        slugToUid.set("manit-arsanok", args.currentUserUid);
      }

      // 2. Create placeholder docs for instructors not yet in /users/
      const usersCreated: string[] = [];
      const batchUser = writeBatch(db);
      let userBatchCount = 0;

      for (const instr of INSTRUCTORS_2568) {
        if (slugToUid.has(instr.slug)) continue;
        const userId = `placeholder_${instr.slug}`;
        const userRef = doc(db, "users", userId);
        batchUser.set(userRef, {
          uid: userId,
          email: instr.email ?? "",
          displayName: instr.name,
          roles: ["instructor"],
          programs: [args.programId],
          dept: instr.dept,
          active: true,
          placeholder: true,
          placeholderSlug: instr.slug,
          createdAt: Timestamp.now(),
          lastLoginAt: null,
        });
        slugToUid.set(instr.slug, userId);
        usersCreated.push(`${instr.name}${instr.email ? ` (${instr.email})` : ""}`);
        userBatchCount++;
      }
      if (userBatchCount > 0) await batchUser.commit();

      // 3. Assign courses (with multi-section support)
      const coursesSnap = await getDocs(collection(db, "programs", args.programId, "courses"));
      const batchCourse = writeBatch(db);
      const assignmentsApplied: string[] = [];
      const skippedNotFound: string[] = [];
      let courseBatchCount = 0;

      const codeToCourseDoc = new Map<string, typeof coursesSnap.docs[0]>();
      for (const cd of coursesSnap.docs) {
        const code = (cd.data().code ?? "") as string;
        if (code) codeToCourseDoc.set(code.replace(/\s+/g, ""), cd);
      }

      for (const a of COURSE_ASSIGNMENTS_2568) {
        const cd = codeToCourseDoc.get(a.code.replace(/\s+/g, ""));
        if (!cd) {
          skippedNotFound.push(a.code);
          continue;
        }

        // Build sections array
        const sections = a.sections.map(sec => {
          const uid = slugToUid.get(sec.slug);
          const name = INSTRUCTORS_2568.find(i => i.slug === sec.slug)?.name;
          if (!uid || !name) return null;
          return {
            section: sec.section,
            instructorUid: uid,
            instructorName: name,
            semester: sec.semester,
          };
        }).filter(Boolean) as Array<{ section: number; instructorUid: string; instructorName: string; semester: string }>;

        if (sections.length === 0) continue;

        // Primary = first section (coordinator)
        const primary = sections[0];

        // All unique instructor UIDs (for array-contains query)
        const allInstructorUids = Array.from(new Set(sections.map(s => s.instructorUid)));

        batchCourse.update(cd.ref, {
          instructorUid: primary.instructorUid,
          instructorName: primary.instructorName,
          sections,
          allInstructorUids,
        });

        const uniqueInstructorNames = Array.from(new Set(sections.map(s => s.instructorName)));
        assignmentsApplied.push(`${a.code} → ${uniqueInstructorNames.length} instructors (${sections.length} sections)`);
        courseBatchCount++;
      }
      if (courseBatchCount > 0) await batchCourse.commit();

      return {
        usersCreated,
        assignmentsApplied: assignmentsApplied.length,
        assignmentsDetail: assignmentsApplied,
        skippedNotFound,
      };
    },

    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["program-courses", vars.programId] });
      qc.invalidateQueries({ queryKey: ["users-list"] });
      qc.invalidateQueries({ queryKey: ["my-courses"] });
    },
  });
}

// ============ Auto-link placeholder when real user logs in ============

/**
 * When a real user logs in, check if there's a placeholder doc matching them.
 * Match priority:
 *   1. Email (most reliable — survives display name variations)
 *   2. displayName exact match (fallback)
 *
 * If found:
 *   - Transfer placeholder's course assignments to real user
 *   - Mark placeholder as deactivated + linked
 */
export async function autoLinkPlaceholderIfMatched(
  realUid: string,
  realDisplayName: string,
  programId: string,
  realEmail?: string,
): Promise<{ linked: boolean; coursesUpdated: number; placeholderSlug?: string; matchedBy?: "email" | "name" }> {
  if (!realDisplayName && !realEmail) return { linked: false, coursesUpdated: 0 };
  const { db } = getFirebase();

  let placeholderDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  let matchedBy: "email" | "name" = "name";

  // 1. Try email match first
  if (realEmail) {
    const emailQ = query(
      collection(db, "users"),
      where("placeholder", "==", true),
      where("email", "==", realEmail.toLowerCase())
    );
    const snap = await getDocs(emailQ);
    if (!snap.empty) {
      placeholderDoc = snap.docs[0];
      matchedBy = "email";
    }
  }

  // 2. Fallback: displayName match
  if (!placeholderDoc && realDisplayName) {
    const nameQ = query(
      collection(db, "users"),
      where("placeholder", "==", true),
      where("displayName", "==", realDisplayName)
    );
    const snap = await getDocs(nameQ);
    if (!snap.empty) {
      placeholderDoc = snap.docs[0];
      matchedBy = "name";
    }
  }

  if (!placeholderDoc) return { linked: false, coursesUpdated: 0 };

  const placeholderUid = placeholderDoc.id;
  const placeholderSlug = (placeholderDoc.data().placeholderSlug ?? "") as string;

  // Find courses where this placeholder appears (as primary OR in any section)
  const allCoursesSnap = await getDocs(collection(db, "programs", programId, "courses"));
  const batch = writeBatch(db);
  let updated = 0;

  for (const c of allCoursesSnap.docs) {
    const cd = c.data();
    let needsUpdate = false;
    const updates: Record<string, unknown> = {};

    // Primary instructor
    if (cd.instructorUid === placeholderUid) {
      updates.instructorUid = realUid;
      needsUpdate = true;
    }

    // Sections
    if (Array.isArray(cd.sections)) {
      const newSections = (cd.sections as Array<{ instructorUid: string; [k: string]: unknown }>).map(s =>
        s.instructorUid === placeholderUid ? { ...s, instructorUid: realUid } : s
      );
      if (JSON.stringify(newSections) !== JSON.stringify(cd.sections)) {
        updates.sections = newSections;
        needsUpdate = true;
      }
    }

    // allInstructorUids
    if (Array.isArray(cd.allInstructorUids)) {
      const newAll = (cd.allInstructorUids as string[]).map(uid => uid === placeholderUid ? realUid : uid);
      if (JSON.stringify(newAll) !== JSON.stringify(cd.allInstructorUids)) {
        updates.allInstructorUids = Array.from(new Set(newAll));
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      batch.update(c.ref, updates);
      updated++;
    }
  }

  // Mark placeholder as inactive (keep audit trail)
  batch.update(placeholderDoc.ref, {
    active: false,
    linkedToRealUid: realUid,
    linkedAt: serverTimestamp(),
  });

  // ALSO: propagate placeholder's canonical name (with title prefix) to real user doc.
  // Firebase Auth displayName usually has no Thai title prefix, but placeholders have
  // the full "ผศ.ดร.ชื่อ สกุล" form. Real user doc should use the canonical name.
  const ph = placeholderDoc.data() as { displayName?: string; position?: string; affiliation?: string; placeholderSlug?: string };
  const realUserRef = doc(db, "users", realUid);
  const propagated: Record<string, unknown> = {};
  if (ph.displayName) propagated.displayName = ph.displayName;
  if (ph.position) propagated.position = ph.position;
  if (ph.affiliation) propagated.affiliation = ph.affiliation;
  if (ph.placeholderSlug) propagated.linkedFromSlug = ph.placeholderSlug;
  if (Object.keys(propagated).length > 0) {
    batch.set(realUserRef, propagated, { merge: true });
  }

  await batch.commit();

  return { linked: true, coursesUpdated: updated, placeholderSlug, matchedBy };
}

/**
 * Sync display names of real (already-logged-in) users from INSTRUCTORS_2568 catalog.
 *
 * Use case: After a user logs in for the first time, Firebase Auth's displayName
 * doesn't include the Thai title prefix (เช่น "ผศ.ดร."). This function finds users
 * by email and updates their displayName to the canonical prefixed form from the
 * catalog.
 *
 * Returns the list of updates so callers can refresh cached names (e.g., in plan.committee).
 */
export async function syncDisplayNamesFromCatalog(): Promise<{
  updated: Array<{ uid: string; email: string; oldName: string; newName: string }>;
  notFound: string[];
}> {
  const { db } = getFirebase();
  const updated: Array<{ uid: string; email: string; oldName: string; newName: string }> = [];
  const notFound: string[] = [];

  for (const ins of INSTRUCTORS_2568) {
    if (!ins.email) continue;
    // Look up real (non-placeholder) user by email
    const q = query(
      collection(db, "users"),
      where("email", "==", ins.email.toLowerCase())
    );
    const snap = await getDocs(q);
    const realUsers = snap.docs.filter(d => {
      const data = d.data();
      return data.placeholder !== true;
    });
    if (realUsers.length === 0) {
      // Also try non-lowercased
      const q2 = query(collection(db, "users"), where("email", "==", ins.email));
      const snap2 = await getDocs(q2);
      const realUsers2 = snap2.docs.filter(d => d.data().placeholder !== true);
      if (realUsers2.length === 0) {
        notFound.push(ins.email);
        continue;
      }
      realUsers.push(...realUsers2);
    }
    for (const u of realUsers) {
      const cur = u.data() as { displayName?: string };
      if (cur.displayName !== ins.name) {
        await setDoc(u.ref, {
          displayName: ins.name,
          dept: ins.dept,
        }, { merge: true });
        updated.push({
          uid: u.id,
          email: ins.email,
          oldName: cur.displayName ?? "",
          newName: ins.name,
        });
      }
    }
  }

  return { updated, notFound };
}
